import { Tooltip, Box } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";

/**
 * Индикатор статуса сохранения. Близнец BookLifetimeChip (тот справа).
 *   idle   — ровный круг с серой точкой (всё сохранено)
 *   saving — круг плавно раскрывается в чип «Сохраняем» (зелёный)
 *   error  — то же, красный «Ошибка»
 * Ничего сам не запрашивает — отражает props saving/error.
 * Анимация через framer-motion (как в остальном проекте).
 */
export default function SaveStatusChip({ saving, error, onClick }) {
  const state = error ? "error" : saving ? "saving" : "idle";

  const cfg = {
    idle: {
      dot: "var(--joy-palette-neutral-400)",
      bg: "rgba(255,255,255,0.92)",
      fg: "var(--joy-palette-text-primary)",
      ring: "none",
      text: "",
      Icon: CloudDoneRoundedIcon,
    },
    saving: {
      dot: "var(--joy-palette-success-500)",
      bg: "color-mix(in srgb, var(--joy-palette-success-100) 70%, #fff)",
      fg: "var(--joy-palette-success-700)",
      ring: "0 0 0 1px color-mix(in srgb, var(--joy-palette-success-500) 25%, transparent)",
      text: "Сохраняем",
      Icon: CloudSyncRoundedIcon,
    },
    error: {
      dot: "#fff",
      bg: "var(--joy-palette-danger-500)",
      fg: "#fff",
      ring: "0 0 0 1px rgba(255,255,255,0.35)",
      text: "Ошибка",
      Icon: CloudOffRoundedIcon,
    },
  }[state];

  const tip = {
    idle:   "Статус сохранения. Все изменения сохраняются автоматически.",
    saving: "Сохраняем изменения…",
    error:  "Не удалось сохранить последнее действие. Проверьте подключение к интернету и повторите.",
  }[state];

  const hasText = !!cfg.text;

  return (
    <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 30 }}>
      <Tooltip title={tip} size="sm" variant="soft" placement="bottom-start" arrow sx={{ maxWidth: 260 }}>
        <motion.div
          role={error ? "button" : "status"}
          tabIndex={error ? 0 : undefined}
          aria-label={error ? "Открыть описание ошибки сохранения" : tip}
          onClick={error ? onClick : undefined}
          onKeyDown={event => {
            if (error && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault(); onClick?.();
            }
          }}
          initial={false}
          animate={{
            backgroundColor: cfg.bg,
            paddingLeft: hasText ? 6 : 7,
            paddingRight: hasText ? 7 : 7,
            boxShadow: cfg.ring !== "none"
              ? `0 2px 10px rgba(0,0,0,0.10), ${cfg.ring}`
              : "0 2px 10px rgba(0,0,0,0.10)",
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            height: 24,
            borderRadius: 999,
            border: "1px solid rgba(0,0,0,0.08)",
            backdropFilter: "blur(4px)",
            cursor: error ? "pointer" : "default",
            overflow: "hidden",
            WebkitTapHighlightColor: "transparent",
            boxSizing: "border-box",
          }}
        >
          {/* Точка-статус: в idle это единственный элемент → идеальный круг */}
          <motion.span
            layout
            animate={{ backgroundColor: cfg.dot }}
            transition={{ duration: 0.2 }}
            style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0 }}
          />

          {/* Текст появляется плавно (fade + slide), при исчезновении — так же */}
          <AnimatePresence initial={false} mode="popLayout">
            {hasText && (
              <motion.span
                key={state}
                initial={{ opacity: 0, x: -6, width: 0 }}
                animate={{ opacity: 1, x: 0, width: "auto" }}
                exit={{ opacity: 0, x: -6, width: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                  color: cfg.fg,
                  font: "600 10px/1 var(--joy-fontFamily-body, -apple-system, Segoe UI, Roboto, sans-serif)",
                  overflow: "hidden",
                }}
              >
                {state === "error" && <cfg.Icon style={{ fontSize: 12, color: cfg.fg }} />}
                {cfg.text}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </Tooltip>
    </Box>
  );
}